import React, { useContext } from "react";
import { expertUrl, productUrl, companyUrl, prProfileUrl } from "views_shared/app/javascript/utils/urls";
import AboutSection from "../../profiles/sections/AboutSection";
import EmploymentSection, { toEmploymentRow } from "../../profiles/sections/EmploymentSection";
import RepresentsSection from "../../profiles/RepresentsSection";
import useProductCardDetails from "../../profiles/data/use_product_card_details";
import Tag from "../../profiles/Tag";
import HashtagsCollapsible from "../../hashtags/HashtagsCollapsible";
import { EXPERT_ALSO_REPRESENTS_SECTION_ID } from "../../experts/show/expert_sections";
import { PrProfileContext } from "../data/pr_profile_context_management";
import usePrExpertsApi from "../data/use_pr_experts_api";
import usePrRepresentationsApi from "../data/use_pr_representations_api";

// The design draws a handful of chips on a person row with no overflow control, so the cap lives
// with the caller rather than in the row. The provider ranks each entity's tags most-tagged first,
// so this takes the top few rather than an arbitrary few.
const ROW_TAG_LIMIT = 3;

// DOM id of the Represents section card, shared with the Source Directory PR cards: a card's
// "Represents" link deep-links to `/pr_profiles/:slug#<this>` so the profile lands scrolled to it.
export const PR_REPRESENTS_SECTION_ID = "pr-section-represents";

// A PR card's "Represents" link. Hybrids are redirected off the PR profile URL to their expert page
// (see PrProfilesController), and the fragment is lost server-side across that 301 — so link them
// straight to the expert page's represents section instead of through the redirect.
export function prCardRepresentsHref(hit) {
  // V1-COMPAT: when V1-hosted, every type links to the PR user page (a hybrid's migrate_id is the
  // V1 PrUser id, not a source id, and V1 renders non-pure-PR types with its own pages anyway).
  if (globalThis.Qwoted?.V2_URL) return prProfileUrl(hit);

  return hit.type_of_profile === "type_promoter_hybrid"
    ? `/experts/${hit.slug}#${EXPERT_ALSO_REPRESENTS_SECTION_ID}`
    : `/pr_profiles/${hit.slug}#${PR_REPRESENTS_SECTION_ID}`;
}

function hashtagIndexUrl(hashtagName) {
  return `/pr_profiles?hashtag=${encodeURIComponent(hashtagName)}`;
}

function buildHashtagUrl(hashtag) {
  return hashtagIndexUrl(hashtag.hashtagName);
}

// A row chip describes the represented expert, so it filters the experts index —
// unlike the page's own expertise chips above, which describe this PR profile.
function rowTags(tags) {
  if (!tags || tags.length === 0) return null;

  return (
    <div className="d-flex flex-wrap gap-1 justify-content-end">
      {tags.slice(0, ROW_TAG_LIMIT).map((tag) => (
        <Tag key={tag.hashtag_id} href={`/experts?hashtag=${encodeURIComponent(tag.hashtag_name)}`}>
          {tag.hashtag_name}
        </Tag>
      ))}
    </div>
  );
}

// PR Profile content, as a vertical stack of shared <SectionCard>s: About (bio, with the profile's
// hashtags as an eyebrow chip row above it), Represents (tabbed experts/products/companies), and
// Employment. The represented collections + employment come from the provider (the single source of
// truth — the edit modals and the section's remove mutate the same collections). Edit affordances stay
// scattered: About + Employment get a pen (bio is edited via the HEADER modal); the Represents section
// carries its own Add/remove.
function PrSections() {
  const context = useContext(PrProfileContext);
  const {
    prProfile,
    representedExperts,
    representedProducts,
    representedCompanies,
    representedExpertTags,
    employments,
    hashtags,
    viewerContext,
    setOpenEditModal,
    generalError,
    setGeneralError,
    activeTabRequest,
    setActiveTabRequest,
  } = context;

  // Adds go through the edit modals (routed by the Add dropdown below); the rows themselves only
  // need their inline-remove handlers here.
  const { removeExpert } = usePrExpertsApi({ ...context });
  const { removeProduct, removeCompany } = usePrRepresentationsApi({ ...context });
  const { details: productDetails, onShow: showProductCards } = useProductCardDetails(
    (representedProducts || []).map((product) => product.id),
  );

  const editable = !!viewerContext?.editable;
  // A job flagged `personHidden` is hidden from this person's own profile, so it must not render here.
  // The provider still holds every job — the edit modal is where hidden ones are managed.
  const jobs = (employments || []).filter((job) => !job.personHidden);

  const representsTabs = [
    {
      key: "experts",
      label: "Experts",
      rows: (representedExperts || []).map((expert) => ({
        id: expert.id,
        href: expertUrl({ slug: expert.slug, migrate_id: expert.migrateId }),
        imageUrl: expert.profileImageUrl,
        title: expert.name,
        subtitleTitle: expert.featuredTitle,
        subtitleCompanyName: expert.featuredCompanyName,
        subtitleCompanyHref: expert.featuredCompanySlug
          ? companyUrl({ slug: expert.featuredCompanySlug, migrate_id: expert.featuredCompanyMigrateId })
          : undefined,
        tags: rowTags((representedExpertTags || {})[expert.id]),
      })),
      onRemove: (row) => removeExpert(row.id),
      emptyText: "No experts represented yet.",
    },
    {
      key: "products",
      label: "Products",
      layout: "cards",
      perPage: 4,
      onShow: showProductCards,
      rows: (representedProducts || []).map((product) => ({
        id: product.id,
        href: productUrl({ slug: product.slug, migrate_id: product.migrateId }),
        imageUrl: product.profileImageUrl,
        title: product.name,
        subtitleCompanyName: product.companyName,
        subtitleCompanyHref: product.companySlug
          ? companyUrl({ slug: product.companySlug, migrate_id: product.companyMigrateId })
          : undefined,
        price: productDetails[product.id]?.price,
        description: productDetails[product.id]?.description,
      })),
      onRemove: removeProduct,
      emptyText: "No products represented yet.",
    },
    {
      key: "companies",
      label: "Companies",
      rows: (representedCompanies || []).map((company) => ({
        id: company.id,
        href: companyUrl({ slug: company.slug, migrate_id: company.migrateId }),
        imageUrl: company.profileImageUrl,
        title: company.name,
      })),
      onRemove: removeCompany,
      emptyText: "No companies represented yet.",
    },
  ];

  return (
    <div className="container-fluid px-0">
      {(editable || prProfile.bio || hashtags.length > 0) && (
        <AboutSection
          id="pr-section-about"
          eyebrowLabel="Covers:"
          eyebrowContent={
            hashtags.length > 0 ? (
              <HashtagsCollapsible hashtags={hashtags} hashtagUrlResolver={buildHashtagUrl} />
            ) : null
          }
          text={prProfile.bio}
          editable={editable}
          onEdit={() => setOpenEditModal("HEADER")}
          editButtonId="modal-button-ABOUT"
        />
      )}

      <RepresentsSection
        id={PR_REPRESENTS_SECTION_ID}
        tabs={representsTabs}
        editable={editable}
        addOptions={
          editable
            ? [
                { label: "Expert", icon: "fa-solid fa-user", onAdd: () => setOpenEditModal("EXPERTS") },
                { label: "Product", icon: "fa-solid fa-box-open", onAdd: () => setOpenEditModal("PRODUCTS") },
                { label: "Company", icon: "fa-solid fa-building", onAdd: () => setOpenEditModal("COMPANIES") },
              ]
            : undefined
        }
        activeTabRequest={activeTabRequest}
        onActiveTabRequestHandled={() => setActiveTabRequest(null)}
        // generalError is shared with the edit modals, so one raised inside an open modal also lands
        // here — harmlessly, behind the backdrop.
        generalError={generalError}
        clearGeneralError={() => setGeneralError(null)}
      />

      {(jobs.length > 0 || editable) && (
        <EmploymentSection
          id="pr-section-employment"
          rows={jobs.map(toEmploymentRow)}
          showDates
          editable={editable}
          onEdit={() => setOpenEditModal("EMPLOYMENT")}
          editButtonId="modal-button-EMPLOYMENT"
        />
      )}
    </div>
  );
}

export default PrSections;
